/**
 * Shared polish primitives for PreachKit panels.
 * Centralizes skeleton loaders, empty states, export menus, and onboarding logic.
 */
import { motion } from "framer-motion";
import { Sparkles, BookOpen, MessageSquare, FileText, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";

export const fadeIn = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.25 },
};

export const stagger = {
  animate: { transition: { staggerChildren: 0.05 } },
};

/** Skeleton block — animated shimmer placeholder */
export function SkelLine({ width = "100%", height = 12, className = "" }) {
  return (
    <div
      className={`bg-slate-700/40 rounded animate-pulse ${className}`}
      style={{ width, height }}
      aria-hidden="true"
    />
  );
}

/** Skeleton for a generated outline result */
export function SkeletonOutline() {
  return (
    <div className="space-y-3" data-testid="skeleton-outline">
      <SkelLine width="60%" height={20} />
      <SkelLine width="40%" height={12} />
      <div className="p-3 rounded bg-amber-500/5 border border-amber-500/10 space-y-2">
        <SkelLine width="20%" height={10} />
        <SkelLine width="80%" />
      </div>
      {[0, 1, 2].map((i) => (
        <div key={i} className="border-l-2 border-amber-500/30 pl-3 space-y-2 py-1">
          <SkelLine width="50%" height={14} />
          <SkelLine width="30%" height={10} />
          <SkelLine width="100%" />
          <SkelLine width="85%" />
        </div>
      ))}
    </div>
  );
}

/** Skeleton for scripture-to-slides deck */
export function SkeletonDeck() {
  return (
    <div className="space-y-2" data-testid="skeleton-deck">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="p-3 rounded border border-slate-800 bg-slate-950/60 space-y-1.5">
          <div className="flex items-center justify-between">
            <SkelLine width={60} height={14} />
            <SkelLine width={20} height={10} />
          </div>
          <SkelLine width="55%" height={16} />
          <SkelLine width="100%" />
          <SkelLine width="90%" />
        </div>
      ))}
    </div>
  );
}

/** Skeleton for a Bible search response */
export function SkeletonSearch() {
  return (
    <div className="space-y-3" data-testid="skeleton-search">
      <SkelLine width="30%" height={12} />
      {[0, 1, 2].map((i) => (
        <div key={i} className="p-3 rounded border border-slate-800 bg-slate-950/40 space-y-2">
          <SkelLine width="40%" height={14} />
          <SkelLine width="100%" height={10} />
          <SkelLine width="70%" height={10} />
        </div>
      ))}
    </div>
  );
}

/** Empty state with example prompts users can click to fill the form. */
export function EmptyState({ icon: Icon = Lightbulb, title, description, examples = [], onPick, testId = "empty-state" }) {
  return (
    <motion.div
      {...fadeIn}
      className="text-center py-10 px-4"
      data-testid={testId}
    >
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-500/10 mb-3">
        <Icon className="w-6 h-6 text-amber-400" />
      </div>
      <h3 className="text-sm font-semibold text-slate-200 mb-1">{title}</h3>
      {description && <p className="text-xs text-slate-500 mb-3 max-w-sm mx-auto">{description}</p>}
      {examples.length > 0 && (
        <div className="flex flex-wrap justify-center gap-1.5 max-w-md mx-auto">
          <span className="text-[10px] uppercase text-slate-600 w-full">Try:</span>
          {examples.map((ex, i) => (
            <Button
              key={i}
              size="sm"
              variant="outline"
              className="h-7 text-[11px] border-amber-500/30 hover:bg-amber-500/10 hover:border-amber-500/60"
              onClick={() => onPick && onPick(ex)}
              data-testid={`${testId}-example-${i}`}
            >
              {ex.label}
            </Button>
          ))}
        </div>
      )}
    </motion.div>
  );
}

export const EXAMPLE_OUTLINES = [
  { label: "Hope in trials (Romans 5)", topic: "Hope in suffering and trials", scripture: "Romans 5:1-5" },
  { label: "Living water (John 4)", topic: "Jesus offers the water of life", scripture: "John 4:13-14" },
  { label: "Prodigal son", topic: "The Father's relentless love", scripture: "Luke 15:11-32" },
  { label: "Sermon on the Mount", topic: "Blessed are the poor in spirit", scripture: "Matthew 5:1-12" },
];

export const EXAMPLE_SCRIPTURES = [
  { label: "Psalm 23", scripture: "Psalm 23" },
  { label: "Romans 8:28", scripture: "Romans 8:28" },
  { label: "John 3:16-21", scripture: "John 3:16-21" },
  { label: "Philippians 4:4-9", scripture: "Philippians 4:4-9" },
];

export const EXAMPLE_SEARCHES = [
  { label: "God's love", query: "God's love" },
  { label: "Forgiveness", query: "forgiveness and grace" },
  { label: "Anxiety", query: "peace and anxiety" },
  { label: "Joy", query: "joy in suffering" },
];

export const EXAMPLE_COMPANION = [
  { label: "Greek for 'love' in 1 Cor 13", text: "What's the Greek word for 'love' used in 1 Corinthians 13 and how does it differ from 'phileo'?" },
  { label: "Historical context of Babylon", text: "Give me the historical context of the Babylonian exile and how it shapes Daniel's prophecy." },
  { label: "Cross-references for grace", text: "What are the key cross-references for 'grace' across Paul's letters?" },
];

/** Build URL helper used everywhere we need to download/render a file from a /api response */
export function downloadString(filename, content, mime = "text/plain;charset=utf-8") {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 200);
}
